import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo
} from "react";
import { GenericNavigationPanelProps, SubNavigation } from "../SubNavigation";
import { localize } from "client_legacy/ndaw/culture";
import { useShouldStartLoadingData } from "./useShouldStartLoadingData";
import { SidebarMenu } from "client/Features/Settings/SidebarMenu/SidebarMenu";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { NoResults } from "client/Components";
import { SubNavigationLink } from "./SubNavigationLink";
import { NavigationLinkContext, NavigationLinkContextData } from "client/Contexts/NavigationLinkContext";
import { useSidebarMenuStore } from "client/Features/Settings/SidebarMenu/sidebarMenuStore";
import { shallowComparison } from "client/Misc/compareUtils";

type SettingsPanelProps = GenericNavigationPanelProps;

export const SettingsPanel = (props: SettingsPanelProps): ReactElement | null => {
	const {
		isVisible,
		isPinned,
		isActive
	} = props;

	const alertManager: AlertManager = useAlerts();

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);

	const {
		model,
		status,
		fetch
	} = useSidebarMenuStore(
		x => ({
			model: x.state,
			status: x.status,
			fetch: x.fetch
		}),
		shallowComparison
	);

	const isRenderedByPage: boolean = isActive;

	const loadingErrorHandler = useCallback(
		(): void => {
			alertManager.showError(localize("_GeneralErrorMessage"));
		},
		[alertManager]
	);

	useEffect(
		(): void => {
			if (!shouldStartLoadingData || isRenderedByPage) {
				return;
			}

			fetch({
				onError: loadingErrorHandler,
			});
		},
		[
			loadingErrorHandler,
			shouldStartLoadingData,
			isRenderedByPage,
			fetch
		]
	);

	const navigationLinkContextData = useMemo(
		(): NavigationLinkContextData => {
			return {
				render(props, ref): ReactElement {
					return (
						<SubNavigationLink
							{...props}
							ref={ref}
						/>
					);
				},
			};
		},
		[]
	);

	if (isRenderedByPage) {
		return null;
	}

	const renderContent = (): ReactElement => {
		if (status === "loading" || !shouldStartLoadingData) {
			return <SubNavigation.LoadingIndicator />;
		}

		return status === "error"
			? (
				<NoResults
					title={localize("_UnableToRetrieveItems")}
					icon="gear"
					message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
					onRetry={
						() => fetch({
							forceReload: true,
							onError: loadingErrorHandler
						})
					}
					height="fill"
				/>
			)
			: (
				<NavigationLinkContext.Provider value={navigationLinkContextData}>
					<SidebarMenu
						selectedItemId={undefined}
						model={model}
					/>
				</NavigationLinkContext.Provider>
			);
	};

	return (
		<>
			<SubNavigation.Header
				kind="settings"
				title={localize("_Settings")}
			/>
			<SubNavigation.Body>
				{renderContent()}
			</SubNavigation.Body>
		</>
	);
};

SettingsPanel.displayName="SettingsPanel";